import { useMutation, useQuery } from '@apollo/client'
import {
    Autocomplete,
    Box,
    Chip,
    IconButton,
    Link,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import {
    DataGridPro,
    GridEventListener,
    GridRenderEditCellParams,
} from '@mui/x-data-grid-pro'
import ColorsAvatarGroup from '../CandidatesTableColors/ColorsAvatarGroup'

import { useTheme } from '@emotion/react'
import { NoteAlt, RemoveCircle } from '@mui/icons-material'
import type {
    GetRostersByNameQuery,
    RosterProfileQuery,
    RosterProfileQueryVariables,
} from '__generated__/graphql'
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import { useSearchParams } from 'react-router-dom'
import ProcessStatusPill from 'recruiter/components/ProcessStatusPill'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { UpdateProcessStatus } from 'recruiter/graphql/updateProcessStatus.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import RostersAutocompleteMultiple from 'shared/components/RostersAutocomplete/RostersAutocompleteMultiple'
import useToast from 'shared/components/Toast/useToast'
import { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getPxAsRem, sortProcessStatusesBySummaryGroup } from 'shared/utils'
import { CandidateRosterNoteDrawer } from '../CandidateRosterNoteDrawer'
import ColorsDropdown from '../CandidatesTableColors/ColorsDropdown'
import {
    AddToRoster,
    GetMissingRosterCandidates,
    GetOverlappingRosterCandidates,
} from './Compare.gql'
import { GetRostersByIds } from 'shared/components/RostersAutocomplete/RostersAutocomplete.gql'

const LIMIT = 10

interface CompareTableProps {
    roster: RosterProfileQuery['roster']
    refetchHeaderData: ApolloQueryRefetchFunction<
        RosterProfileQuery,
        RosterProfileQueryVariables
    >
}

const CompareTable: FC<CompareTableProps> = ({ roster, refetchHeaderData }) => {
    const theme = useTheme()
    const toast = useToast()
    const [searchParams, setSearchParams] = useSearchParams()

    const urlCompareMethod = searchParams.get('compareMethod') as
        | 'overlapping'
        | 'missing'
        | null
    const urlRosterIds =
        searchParams.get('compareRosterIds')?.split(',').filter(Boolean) || []

    const [selectedCompareMethod, setSelectedCompareMethodState] = useState<
        'overlapping' | 'missing'
    >(urlCompareMethod || 'overlapping')
    const [selectedRosters, setSelectedRostersState] = useState<
        GetRostersByNameQuery['rostersByName']['data']
    >([])

    const setSelectedCompareMethod = useCallback(
        (method: 'overlapping' | 'missing') => {
            setSelectedCompareMethodState(method)
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev)
                    next.set('compareMethod', method)
                    return next
                },
                { replace: false }
            )
        },
        [setSearchParams]
    )

    const setSelectedRosters = useCallback(
        (
            rostersOrUpdater:
                | GetRostersByNameQuery['rostersByName']['data']
                | ((
                      prev: GetRostersByNameQuery['rostersByName']['data']
                  ) => GetRostersByNameQuery['rostersByName']['data'])
        ) => {
            setSelectedRostersState((prevRosters) => {
                const newRosters =
                    typeof rostersOrUpdater === 'function'
                        ? rostersOrUpdater(prevRosters)
                        : rostersOrUpdater
                setSearchParams(
                    (prev) => {
                        const next = new URLSearchParams(prev)
                        if (newRosters.length > 0) {
                            next.set(
                                'compareRosterIds',
                                newRosters.map((r) => r.id).join(',')
                            )
                        } else {
                            next.delete('compareRosterIds')
                        }
                        return next
                    },
                    { replace: false }
                )
                return newRosters
            })
        },
        [setSearchParams]
    )

    const [selectedRosterCandidateId, setSelectedRosterCandidateId] =
        useState('')
    const [openRosterNoteDrawer, setOpenRosterNoteDrawer] = useState(false)
    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)

    const { data: rostersData } = useQuery(GetRostersByIds, {
        variables: { ids: urlRosterIds.filter((id) => id !== roster?.id) },
        skip: urlRosterIds.length === 0,
    })

    useEffect(() => {
        if (rostersData?.rostersByIds) {
            setSelectedRostersState(rostersData.rostersByIds)
        }
    }, [rostersData])

    const {
        data,
        fetchMore: fetchMoreOverlapping,
        refetch,
    } = useQuery(GetOverlappingRosterCandidates, {
        fetchPolicy: 'network-only',
        variables: {
            rosterId: roster?.id || '',
            otherRosterIds: selectedRosters.map((roster) => roster.id),
            page: 1,
            limit: LIMIT,
        },
        skip: selectedCompareMethod !== 'overlapping',
    })

    const {
        data: missingData,
        fetchMore: fetchMoreMissing,
        refetch: refetchMissing,
    } = useQuery(GetMissingRosterCandidates, {
        fetchPolicy: 'network-only',
        variables: {
            rosterId: roster?.id || '',
            otherRosterIds: selectedRosters.map((roster) => roster?.id),
            page: 1,
            limit: LIMIT,
        },
        skip: selectedCompareMethod !== 'missing',
    })

    const [updateProcessStatus] = useMutation(UpdateProcessStatus)
    const [addToRoster] = useMutation(AddToRoster)

    const rows = useMemo(() => {
        if (selectedCompareMethod === 'overlapping') {
            return (
                data?.overlappingRosterCandidates?.data?.map(
                    (rosterCandidate) => ({
                        id: rosterCandidate.rosterCandidate.candidateId,
                        rosterCandidateIds: [
                            rosterCandidate.rosterCandidate.id,
                        ],
                        rosterCandidateId: rosterCandidate.rosterCandidate.id,
                        status: rosterCandidate?.rosterCandidate
                            ?.candidateProcessStatus?.processStatus,
                        candidateId:
                            rosterCandidate.rosterCandidate.candidateId,
                        colors: rosterCandidate.rosterCandidate.candidateColors?.map(
                            (cc) => cc?.rosterColor
                        ),
                        firstName:
                            rosterCandidate.rosterCandidate.candidate?.user
                                ?.nickname ||
                            rosterCandidate.rosterCandidate.candidate?.user
                                ?.firstName,
                        lastName:
                            rosterCandidate.rosterCandidate.candidate?.user
                                ?.lastName,
                        rosterStatuses:
                            rosterCandidate.referenceRosterCandidates?.reduce(
                                (acc, curr) => {
                                    acc[curr.rosterId] =
                                        curr.candidateProcessStatus?.processStatus
                                    return acc
                                },
                                {}
                            ),
                    })
                ) || []
            )
        } else {
            return (
                missingData?.missingRosterCandidates?.data?.map(
                    (rosterCandidateComparison) => ({
                        id: rosterCandidateComparison.candidate?.id,
                        candidateId: rosterCandidateComparison.candidate?.id,
                        firstName:
                            rosterCandidateComparison.candidate?.user
                                ?.nickname ||
                            rosterCandidateComparison.candidate?.user
                                ?.firstName,
                        lastName:
                            rosterCandidateComparison.candidate?.user?.lastName,
                        rosterStatuses:
                            rosterCandidateComparison.referenceRosterCandidates?.reduce(
                                (acc, curr) => {
                                    acc[curr.rosterId] =
                                        curr.candidateProcessStatus?.processStatus
                                    return acc
                                },
                                {}
                            ),
                    })
                ) || []
            )
        }
    }, [data, missingData, selectedCompareMethod])

    const ProcessStatusEditCell = useCallback(
        (params: GridRenderEditCellParams) => {
            const handleValueChange = async (value) => {
                let success: boolean
                let message: string
                if (selectedCompareMethod === 'missing') {
                    const { data } = await addToRoster({
                        variables: {
                            candidateIds: [params.row?.candidateId],
                            rosterId: roster?.id || '',
                            processStatusId: value?.id,
                        },
                    })
                    success = data?.addCandidatesToRoster.success || false
                    message = data?.addCandidatesToRoster.message || ''
                } else {
                    const { data } = await updateProcessStatus({
                        variables: {
                            rosterId: roster?.id ?? '',
                            rosterCandidateIds: [params.row.rosterCandidateId],
                            processStatus: value.status,
                        },
                    })
                    success =
                        data?.updateCandidateProcessStatus.success || false
                    message = data?.updateCandidateProcessStatus.message || ''
                }
                if (success) {
                    if (selectedCompareMethod === 'missing') {
                        if (params.api.getRowsCount() === 1) {
                            /* When we remove a row, MUI grid automatically sets focus to that same index. If we have removed the final row, this behavior will throw an error unless we manually direct the focus ourselves using this call: */
                            params.api.setColumnHeaderFocus(
                                selectedRosters[0]?.id
                            )
                        }
                        refetchMissing()
                    } else {
                        refetch()
                        params.api.stopCellEditMode({
                            field: 'rosterStatus',
                            id: params.id,
                        })
                    }
                    toast({
                        title: 'Success',
                        message:
                            message ||
                            'Candidate process status updated successfully',
                        severity: 'success',
                    })
                    // also refetch header data to update donut graph due to new statuses
                    refetchHeaderData()
                } else {
                    toast({
                        title: 'Error',
                        message: message || 'Error updating process status',
                        severity: 'error',
                    })
                }
            }
            return (
                <Autocomplete
                    onChange={(event, value) => handleValueChange(value)}
                    options={sortProcessStatusesBySummaryGroup(
                        processStatusOptions?.processStatuses ?? []
                    )}
                    value={params.row.status ?? null}
                    getOptionLabel={(option) => option.statusLabel}
                    isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                    }
                    groupBy={(option) =>
                        option.processStatusSummaryGroup?.summaryGroup || ''
                    }
                    disableClearable
                    fullWidth
                    sx={
                        selectedCompareMethod === 'missing'
                            ? {
                                  marginTop: '4px',
                              }
                            : {
                                  '& .MuiFormControl-root': {
                                      height: '100%',
                                  },
                                  '& .MuiInputBase-root': {
                                      height: '100%',
                                      borderRadius: '0px',
                                  },
                              }
                    }
                    renderInput={(renderParams) => (
                        <TextField
                            {...renderParams}
                            fullWidth
                            placeholder={
                                selectedCompareMethod === 'missing'
                                    ? 'Add to Roster'
                                    : undefined
                            }
                            inputProps={{
                                ...renderParams.inputProps,
                                height: '100%',
                            }}
                        />
                    )}
                />
            )
        },
        [
            processStatusOptions,
            refetch,
            toast,
            updateProcessStatus,
            addToRoster,
            refetchMissing,
            roster?.id,
            selectedCompareMethod,
            selectedRosters,
            refetchHeaderData,
        ]
    )

    const handleInfiniteScroll: GridEventListener<'rowsScrollEnd'> = () => {
        const existingRecords =
            data?.overlappingRosterCandidates?.data?.length || 0
        if (
            existingRecords ===
            data?.overlappingRosterCandidates?.pageInfo?.itemTotal
        ) {
            return
        } else {
            fetchMoreOverlapping({
                variables: {
                    page: Math.ceil(existingRecords / LIMIT) + 1,
                    limit: LIMIT,
                },
                updateQuery(previousData, { fetchMoreResult }) {
                    if (!fetchMoreResult) return previousData
                    return {
                        ...previousData,
                        overlappingRosterCandidates: {
                            data: [
                                ...previousData?.overlappingRosterCandidates
                                    .data,
                                ...fetchMoreResult?.overlappingRosterCandidates
                                    .data,
                            ],
                            pageInfo:
                                fetchMoreResult.overlappingRosterCandidates
                                    .pageInfo,
                        },
                    }
                },
            })
        }
    }

    const handleInfiniteScrollMissing: GridEventListener<
        'rowsScrollEnd'
    > = () => {
        const existingRecords =
            missingData?.missingRosterCandidates?.data?.length || 0
        if (
            existingRecords ===
            missingData?.missingRosterCandidates?.pageInfo?.itemTotal
        ) {
            return
        } else {
            fetchMoreMissing({
                variables: {
                    page: Math.ceil(existingRecords / LIMIT) + 1,
                    limit: LIMIT,
                },
                updateQuery(previousData, { fetchMoreResult }) {
                    if (!fetchMoreResult) return previousData
                    return {
                        ...previousData,
                        missingRosterCandidates: {
                            data: [
                                ...previousData?.missingRosterCandidates.data,
                                ...fetchMoreResult?.missingRosterCandidates
                                    .data,
                            ],
                            pageInfo:
                                fetchMoreResult.missingRosterCandidates
                                    .pageInfo,
                        },
                    }
                },
            })
        }
    }

    const compareColumnDef = useMemo(() => {
        const sharedColumns = [
            {
                field: 'firstName',
                editable: false,
                sortable: false,
                width: 140,
                renderCell: (params) => (
                    <Link
                        href={`${recruiterRouteConfig.candidates.path}/${params.row.candidateId}`}
                        sx={{
                            textDecoration: 'none',
                        }}
                    >
                        {params.value}
                    </Link>
                ),
                headerName: roster?.rosterName,
                headerClassName: 'overflow-header',
                resizable: false,
            },
            {
                field: 'lastName',
                headerName: '',
                editable: false,
                sortable: false,
                width: 140,
                renderCell: (params) => (
                    <Link
                        href={`${recruiterRouteConfig.candidates.path}/${params.row.candidateId}`}
                        sx={{
                            textDecoration: 'none',
                        }}
                    >
                        {params.value}
                    </Link>
                ),
                renderHeader: () => null,
                headerClassName: 'no-select',
                resizable: false,
            },
        ]

        const referenceRostersColumns = selectedRosters.map((roster) => ({
            id: roster.id,
            headerName: roster.rosterName,
            editable: false,
            sortable: false,
            flex: 1,
            field: roster.id,
            renderCell: (params) => {
                const status = params.row.rosterStatuses[roster.id]
                if (!status) return null
                return (
                    <ProcessStatusPill
                        status={status.status}
                        statusLabel={status.statusLabel}
                        summaryGroup={
                            status.processStatusSummaryGroup?.summaryGroup
                        }
                        summaryGroupLabel={
                            status.processStatusSummaryGroup?.label
                        }
                    />
                )
            },
            renderHeader: () => (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexGrow={1}
                    gap={1}
                    width="100%"
                >
                    <Typography
                        sx={{
                            flexShrink: 1,
                            overflow: 'hidden',
                            textWrap: 'nowrap',
                            textOverflow: 'ellipsis',
                            maxWidth: 'calc(100% - 48px)',
                        }}
                        variant="subtitle2"
                    >
                        {roster.rosterName}
                    </Typography>
                    <IconButton
                        onClick={() =>
                            setSelectedRosters((prev) =>
                                [...prev].filter(
                                    (selected) => selected.id !== roster.id
                                )
                            )
                        }
                    >
                        <RemoveCircle color="error" />
                    </IconButton>
                </Stack>
            ),
        }))
        if (selectedCompareMethod === 'overlapping') {
            const overlappingColumnDef = [
                {
                    field: 'colors',
                    headerName: '',
                    sortable: false,
                    editable: true,
                    flex: 1,
                    renderCell: (params) => (
                        <ColorsAvatarGroup
                            colors={params.value}
                            id={params.id}
                        />
                    ),
                    renderEditCell: (params) => (
                        <ColorsDropdown
                            colors={params.value}
                            rosterCandidateId={params.row.rosterCandidateIds[0]}
                            refetchQueries={[GetOverlappingRosterCandidates]}
                            {...params}
                        />
                    ),
                    renderHeader: () => null,
                    headerClassName: 'no-select',
                    resizable: false,
                },
                {
                    field: 'notes',
                    headerName: '',
                    sortable: false,
                    editable: false,
                    flex: 1,
                    renderCell: (params) => (
                        <Stack
                            direction="row"
                            gap={getPxAsRem(4)}
                            alignItems="center"
                            height="100%"
                        >
                            <IconButton
                                onClick={() => {
                                    setOpenRosterNoteDrawer(true)
                                    setSelectedRosterCandidateId(
                                        params.row.rosterCandidateId
                                    )
                                }}
                            >
                                <NoteAlt />
                            </IconButton>
                        </Stack>
                    ),
                    renderHeader: () => null,
                    headerClassName: 'no-select',
                    resizable: false,
                },
                {
                    editable: true,
                    sortable: false,
                    flex: 1,
                    field: 'rosterStatus',
                    renderHeader: () => null,
                    headerClassName: 'no-select',
                    resizable: false,
                    renderEditCell: ProcessStatusEditCell,
                    renderCell: (params) => {
                        const status = params.row.status
                        if (!status) return null
                        return (
                            <ProcessStatusPill
                                status={status.status}
                                statusLabel={status.statusLabel}
                                summaryGroup={
                                    status.processStatusSummaryGroup
                                        ?.summaryGroup
                                }
                                summaryGroupLabel={
                                    status.processStatusSummaryGroup?.label
                                }
                            />
                        )
                    },
                },
            ]
            return [
                ...sharedColumns,
                ...overlappingColumnDef,
                ...referenceRostersColumns,
            ]
        } else {
            const missingColumnDef = [
                {
                    field: 'addButton',
                    headerName: '',
                    sortable: false,
                    editable: false,
                    width: 200,
                    renderCell: ProcessStatusEditCell,
                    renderHeader: () => null,
                    headerClassName: 'no-select',
                    resizable: false,
                },
            ]
            return [
                ...sharedColumns,
                ...missingColumnDef,
                ...referenceRostersColumns,
            ]
        }
    }, [
        selectedRosters,
        ProcessStatusEditCell,
        selectedCompareMethod,
        roster?.rosterName,
    ])

    return (
        <>
            <Stack
                sx={{
                    padding: getPxAsRem(16),
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                }}
            >
                <Box sx={{ width: getPxAsRem(300) }}>
                    <RostersAutocompleteMultiple
                        currentRosterId={roster?.id}
                        ChipProps={{ size: 'small' }}
                        fullWidth
                        renderTags={(selectedRosters) => {
                            if (selectedRosters.length)
                                return (
                                    <Chip
                                        label={`+${selectedRosters.length}`}
                                        title={selectedRosters
                                            .map((roster) => roster.rosterName)
                                            .join(', ')}
                                    />
                                )
                            return null
                        }}
                        limitTags={0}
                        getOptionDisabled={(option) =>
                            selectedRosters.findIndex(
                                (roster) => roster.id === option.id
                            ) === -1 && selectedRosters.length >= 4
                        }
                        initialValue={selectedRosters}
                        onMultipleSelectionChange={(newValue) =>
                            setSelectedRosters(newValue)
                        }
                        allMyRosters={true}
                        getOptionLabel={(option) => option.rosterName}
                        label="Reference Searches"
                        placeholder="Add reference search..."
                    />
                </Box>
                <ToggleButtonGroup
                    value={selectedCompareMethod}
                    exclusive
                    color="primary"
                    size="small"
                >
                    <ToggleButton
                        value="overlapping"
                        onClick={() => setSelectedCompareMethod('overlapping')}
                        color="primary"
                        size="small"
                    >
                        Overlapping
                    </ToggleButton>
                    <ToggleButton
                        value="missing"
                        onClick={() => setSelectedCompareMethod('missing')}
                        color="primary"
                        size="small"
                    >
                        Missing
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            <Box sx={{ display: 'flex', gap: getPxAsRem(24), width: '100%' }}>
                <Box
                    sx={{
                        height: 'inherit',
                        overflow: 'hidden',
                        maxHeight: '75vh',
                        flexGrow: 1,
                        display: 'flex',
                        // overwrite MUI styles for headers to match design (paired with headerClassName property in columnDef)
                        '.overflow-header': {
                            overflow: 'visible',
                            zIndex: `10 !important`,
                        },
                        '.no-select, .overflow-header': {
                            pointerEvents: 'none',
                        },
                        '.overflow-header .MuiDataGrid-columnHeaderTitleContainer, .overflow-header .MuiDataGrid-columnHeaderTitleContainerContent, .overflow-header .MuiDataGrid-columnHeaderTitle':
                            {
                                overflow: 'visible',
                            },
                        '.overflow-header .MuiDataGrid-columnSeparator, .no-select .MuiDataGrid-columnSeparator':
                            {
                                display: 'none !important',
                            },
                    }}
                >
                    <DataGridPro
                        sx={{ minHeight: '40vh' }}
                        columns={compareColumnDef}
                        rows={rows}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        hideFooter
                        onRowsScrollEnd={
                            selectedCompareMethod === 'missing'
                                ? handleInfiniteScrollMissing
                                : handleInfiniteScroll
                        }
                        pinnedColumns={{
                            left: [
                                'firstName',
                                'lastName',
                                'colors',
                                'notes',
                                'rosterStatus',
                                'addButton',
                            ],
                        }}
                    />
                </Box>
                {selectedRosters.length === 0 && (
                    <Typography
                        color={theme.palette.other.secondary_light}
                        width="40%"
                    >
                        Select reference searches to add a roster for comparison
                    </Typography>
                )}
            </Box>
            <CandidateRosterNoteDrawer
                canEdit={roster?.permissions?.canEdit ?? false}
                isOpen={openRosterNoteDrawer}
                onClose={() => setOpenRosterNoteDrawer(false)}
                rosterCandidateId={selectedRosterCandidateId}
            />
        </>
    )
}

export default CompareTable
